import * as React from 'react';
import clsx from 'clsx';
import type { Echo } from '@/types';
import { EchoAvatar } from '../../common/EchoAvatar';
import { ChatStatsBadge } from '../ChatStatsBadge';
import { Badge } from '../../common/Badge';
import { HeaderCredits } from '../../common/HeaderCredits';
import { ClearChatButton } from './ClearChatButton';
import { AudioToggleButton } from './AudioToggleButton';
import { VideoToggleButton } from './VideoToggleButton';
import { getEchoDomain } from '@/utils/echoUtils';
import { featureFlags } from '@/utils/featureFlags';

interface NormalModeLayoutProps {
  echo: Echo;
  avatarSrc: string | null;
  videoAvatarSrc: string | undefined;
  formattedFans: string;
  isGroupChat: boolean;
  isAudioEnabled: boolean;
  onAudioToggle: () => void;
  isVideoEnabled: boolean;
  onVideoToggle: () => void;
  videoMessagesEnabled: boolean;
  onClear?: (() => void) | undefined;
  onClearModalOpen: () => void;
  disabled?: boolean | undefined;
  compact: boolean;
}

export const NormalModeLayout: React.FC<NormalModeLayoutProps> = ({
  echo,
  avatarSrc,
  videoAvatarSrc,
  formattedFans,
  isGroupChat,
  isAudioEnabled,
  onAudioToggle,
  isVideoEnabled,
  onVideoToggle,
  videoMessagesEnabled,
  onClear,
  onClearModalOpen,
  disabled,
  compact,
}) => {
  const categoryDisplay = getEchoDomain(echo);

  return (
    <div className='flex w-full flex-col items-center xl:flex-row xl:justify-between'>
      <div className='flex w-full flex-col items-center xl:w-auto xl:flex-row xl:items-center'>
        <div
          className={clsx(
            'relative z-10 flex w-full',
            'flex-col items-center',
            'xl:mt-0 xl:w-auto xl:flex-row xl:items-center xl:gap-4'
          )}
        >
          <div className='mt-[72px] flex w-full items-start justify-between px-[18px] pt-[16px] xl:hidden'>
            <div className='flex items-start gap-3'>
              <div className='shrink-0'>
                <EchoAvatar
                  src={avatarSrc}
                  videoSrc={
                    featureFlags.videoEnabled && isVideoEnabled ? (videoAvatarSrc ?? null) : null
                  }
                  alt={echo.name}
                  initials={echo.name.slice(0, 2)}
                  enableLeftWaves={true}
                  size={55}
                  iconMode={isGroupChat}
                />
              </div>

              <div className='flex flex-col items-start gap-[6px]'>
                <div className='flex items-center gap-2 pb-0 xl:pb-3'>
                  <h2 className='text-left font-proxima text-16 font-bold leading-105 tracking-normal text-white'>
                    {echo.name}
                  </h2>
                </div>
                <div className='flex items-center gap-2'>
                  <span className='shrink-0 font-proxima text-[11px] font-bold uppercase leading-105 tracking-normal text-white'>
                    D.ECHO
                  </span>
                  <div className='size-[6px] shrink-0 rounded-full bg-accent-200' />
                  {categoryDisplay && <CategoryTooltip label={categoryDisplay} />}
                  {!isGroupChat && (
                    <Badge
                      label={`${formattedFans} Fans`}
                      variant='chatHeader'
                      className='shrink-0 text-[10px] xl:text-[12px]'
                    />
                  )}
                  {!isGroupChat && (
                    <ChatStatsBadge
                      echoId={echo.id}
                      variant='header'
                      className='shrink-0 [&_span]:text-[10px] [&_span]:xl:text-[12px]'
                    />
                  )}
                </div>

                <div className='flex items-center gap-2'>
                  <AudioToggleButton isEnabled={isAudioEnabled} onClick={onAudioToggle} />
                  {featureFlags.videoEnabled && videoMessagesEnabled && (
                    <VideoToggleButton isEnabled={isVideoEnabled} onClick={onVideoToggle} />
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Desktop Layout */}
          <div className='hidden xl:block'>
            <EchoAvatar
              src={avatarSrc}
              videoSrc={
                featureFlags.videoEnabled && isVideoEnabled ? (videoAvatarSrc ?? null) : null
              }
              alt={echo.name}
              initials={echo.name.slice(0, 2)}
              enableLeftWaves={true}
              size={compact ? 50 : 80}
              iconMode={isGroupChat}
            />
          </div>

          <div className={clsx('hidden min-w-0 flex-col items-start text-left xl:flex')}>
            <div className={clsx('flex items-center gap-3', compact ? 'pb-1' : 'pb-[10px]')}>
              <h2
                className={clsx(
                  'font-proxima font-bold tracking-normal text-white',
                  compact ? 'max-w-[180px] truncate text-14' : 'text-header-title'
                )}
              >
                {echo.name}
              </h2>
              {!isGroupChat && <Badge label={`${formattedFans} Fans`} variant='chatHeader' />}
              {!isGroupChat && <ChatStatsBadge echoId={echo.id} variant='header' />}
            </div>
            <div className={clsx('mt-0 flex items-center', compact ? 'gap-2' : 'gap-3')}>
              <span
                className={clsx(
                  'font-proxima font-bold uppercase leading-105 tracking-normal text-white',
                  compact ? 'text-[11px]' : 'text-14'
                )}
              >
                D.ECHO
              </span>
              <div
                className={clsx(
                  'rounded-full bg-accent-200',
                  compact ? 'size-[6px]' : 'size-[10px]'
                )}
              />
              {categoryDisplay && (
                <span
                  className={clsx(
                    'font-sans font-normal text-white',
                    compact ? 'text-[11px]' : 'text-14'
                  )}
                >
                  {categoryDisplay}
                </span>
              )}
              <AudioToggleButton isEnabled={isAudioEnabled} onClick={onAudioToggle} />
              {featureFlags.videoEnabled && videoMessagesEnabled && (
                <VideoToggleButton isEnabled={isVideoEnabled} onClick={onVideoToggle} />
              )}
            </div>
          </div>
        </div>
      </div>

      <div className='relative z-10 hidden items-center gap-3 xl:flex'>
        <HeaderCredits />
        {onClear && <ClearChatButton onClick={onClearModalOpen} disabled={disabled} />}
      </div>
    </div>
  );
};

/** Truncated category label that shows a tooltip on click. */
const CategoryTooltip: React.FC<{ label: string }> = ({ label }) => {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  React.useEffect(() => {
    if (open) {
      const timer = setTimeout(() => setOpen(false), 2500);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [open]);

  return (
    <div ref={ref} className='relative max-w-[80px]'>
      <button
        type='button'
        onClick={() => setOpen((prev) => !prev)}
        className='w-full truncate text-left font-sans text-[11px] font-normal leading-27 tracking-normal text-gray-100'
      >
        {label}
      </button>
      {open && (
        <span
          className={clsx(
            'absolute left-1/2 top-full z-50 mt-1 -translate-x-1/2',
            'whitespace-nowrap rounded-5 bg-black px-2 py-1',
            'font-sans text-10 font-semibold text-white'
          )}
        >
          {label}
        </span>
      )}
    </div>
  );
};
